import { Link } from '@tanstack/react-router'
import { Button } from '#/components/ui/button'
import aboutDecor from '#/assets/about-decor.svg'
import headerIcon from '#/assets/header-icon.svg'
import { HighlightText } from '#/components/common/HighlightText'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { CgMoreO } from 'react-icons/cg'
import { FaPlay } from 'react-icons/fa6'
import { TextAnimate } from '#/components/common/TextAnimate'
import { ABOUT_CONTENT, ABOUT_HIGHLIGHTS, ABOUT_MEDIA } from '#/data/homeAbout'

export type AboutSectionProps = {
  showButton?: boolean
  buttonLabel?: string
  buttonTo?: string
  buttonHash?: string
  onButtonClick?: () => void
}

export default function AboutSection({
  showButton = true,
  buttonLabel = ABOUT_CONTENT.buttonLabel,
  buttonTo = '/about',
  buttonHash,
  onButtonClick,
}: AboutSectionProps) {
  return (
    <section className="relative w-full pb-15 pt-15" id="about">
      <div className="container mx-auto flex flex-col items-center">
        <img
          alt=""
          className="pointer-events-none absolute left-0 top-0 lg:w-2/5 w-3/4 max-lg:origin-top-left"
          src={aboutDecor}
        />

        <ScrollReveal className="flex flex-col items-center justify-center gap-3 max-lg:w-full">
          <RevealItem>
            <img alt="" src={headerIcon} />
          </RevealItem>
          <RevealItem>
            <p className="text-xl font-heading font-bold leading-[normal] text-primary-900 text-center whitespace-nowrap max-sm:text-base max-sm:whitespace-normal">
              {ABOUT_CONTENT.eyebrow}
            </p>
          </RevealItem>
          <RevealItem>
            <h2 className="text-center text-6xl font-bold leading-[normal] text-neutral-900 max-md:text-5xl max-sm:text-4xl">
              {ABOUT_CONTENT.title}
              <HighlightText
                inView
                className="ml-2 bg-linear-to-r from-primary-100/70 to-primary-300/70 text-neutral-900"
                text={ABOUT_CONTENT.highlight}
              />
            </h2>
          </RevealItem>
        </ScrollReveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 w-full items-center gap-15 max-lg:flex-col max-lg:items-start max-lg:gap-10">
          <ScrollReveal className="relative flex flex-1 flex-col items-center justify-center gap-12.5 w-full max-w-156 mx-auto max-lg:h-auto max-lg:w-full max-lg:max-w-130">
            <RevealItem direction="left">
              <div className="relative group overflow-hidden rounded-[28px] border-4 border-white bg-neutral-950 shadow-2xl">
                <img
                  alt={ABOUT_MEDIA.imageAlt}
                  src={ABOUT_MEDIA.image}
                  className="w-full h-auto object-cover rounded-[24px] transition-transform duration-700 ease-out group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  width={600}
                  height={448}
                />

                {/* Simple modern gradient overlay */}
                <div className="pointer-events-none absolute inset-0 rounded-[24px] bg-gradient-to-t from-primary-600/50 via-primary-600/15 to-transparent transition-opacity duration-300" />

                {/* Floating pill badge */}
                <div className="absolute bottom-5 left-5 z-10 flex items-center gap-2 rounded-xl bg-black/40 px-3.5 py-1.5 backdrop-blur-md border border-white/15 shadow-md max-sm:bottom-3 max-sm:left-3">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-semibold text-white tracking-wide max-sm:text-[10px]">
                    Live Interactive Classes
                  </span>
                </div>

                {/* Animated Pulsing Play Icon */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center cursor-pointer">
                  {/* Slow graceful ripple rings */}
                  <span className="absolute size-24 rounded-full bg-primary-400/25 animate-ripple-ping" />
                  <span className="absolute size-24 rounded-full bg-primary-500/20 animate-ripple-ping-slow" />
                  <span className="absolute -inset-2.5 rounded-full border border-primary-300/30 animate-pulse" />

                  {/* Core glowing play button */}
                  <div className="relative flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 text-white shadow-[0_0_30px_rgba(255,182,6,0.55)] transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_50px_rgba(255,182,6,0.85)] animate-ripple-pulse">
                    <FaPlay className="ml-1 text-2xl text-white drop-shadow-md" />
                  </div>
                </div>
              </div>
            </RevealItem>
          </ScrollReveal>

          <ScrollReveal
            className="flex flex-col items-start gap-7.5 max-lg:w-full"
            stagger={0.14}
          >
            <RevealItem>
              <div className="text-base font-normal leading-[normal] text-neutral-500">
                <TextAnimate
                  by="word"
                  animation="fadeIn"
                  duration={1.2}
                  className="mb-2"
                >
                  {ABOUT_CONTENT.paragraphs[0]}
                </TextAnimate>
                <TextAnimate
                  by="word"
                  animation="fadeIn"
                  delay={1.2}
                  duration={1.2}
                  className="mb-2"
                >
                  {ABOUT_CONTENT.paragraphs[1]}
                </TextAnimate>
              </div>
            </RevealItem>
            {ABOUT_HIGHLIGHTS.map((item) => (
              <RevealItem key={item.title}>
                <div className="flex w-full items-center gap-6 max-sm:flex-col max-sm:items-start">
                  <div className="flex size-20 min-w-20 items-center rounded-[999px] border-2 border-primary-500 bg-white p-4">
                    <img
                      alt={item.iconAlt}
                      className="h-auto w-12"
                      src={item.icon}
                      loading="lazy"
                      decoding="async"
                      width={48}
                      height={48}
                    />
                  </div>
                  <div className="flex w-full max-w-lg flex-col items-start justify-center gap-1.5 max-lg:w-full">
                    <h3 className="text-[30px] font-bold leading-[normal] text-neutral-800">
                      {item.title}
                    </h3>
                    <p className="text-base font-normal leading-[normal] text-neutral-500">
                      {item.description}
                    </p>
                  </div>
                </div>
              </RevealItem>
            ))}

            {showButton && (
              <RevealItem>
                <Button asChild className="gap-2 px-5 py-3.5" variant="outline">
                  <Link
                    to={buttonTo}
                    hash={buttonHash}
                    onClick={onButtonClick}
                    aria-label="Read more about Wa'allamah Institute"
                  >
                    <CgMoreO />
                    <span>{buttonLabel}</span>
                    <span className="sr-only"> about Wa'allamah Institute</span>
                  </Link>
                </Button>
              </RevealItem>
            )}
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
